import React, { useEffect, useRef, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView, // Add back for ref typing
  ActivityIndicator,
  Alert,
  Platform,
  Animated,
  TouchableOpacity,
  Dimensions,
} from "react-native";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store";
import { useSelectedLocation } from "../../hooks";
import ShiftHandoverQuestions from "./ShiftHandoverQuestions";
import { fetchShiftHandoverQuestions } from "./fetchShiftHandoverQuestions";
import { ShiftHandoverRecord } from "./fetchShiftHandoverData";
import ShiftHandOverDetailHeader from "./ShiftHandOverDetailHeader";
import ShiftHandOverNotes from "./ShiftHandOverNotes";
import ShiftHandOverMiscellaneousObservations from "./ShiftHandOverMiscellaneousObservations";
import ShiftHandOverAction from "./ShiftHandOverAction";
import ShiftHandOverSignature from "./ShiftHandOverSignature";
import ShiftHandoverErrorModal from "./components/ShiftHandoverErrorModal";
import {
  updateShiftHandoverState,
  fetchAndUpdateShiftHandoverDetails,
} from "./updateShiftHandoverStateUtil";
import { useTheme } from "../../theme";

interface ShiftHandOverDetailsProps {
    isVisible: boolean;
    selectedHandover: ShiftHandoverRecord | null;
    onBackPress: () => void;
    onHomePress?: () => void; // Add this prop for home navigation
    forceDisabled?: boolean; // NEW: force disabled state
}

const ShiftHandOverDetails: React.FC<ShiftHandOverDetailsProps> = ({
    isVisible,
    selectedHandover,
    onBackPress,
    onHomePress, // Add this prop for home navigation
    forceDisabled = false // default to false
}) => {
  console.log('[DEBUG] Render ShiftHandOverDetails'); // Top-level render log
  const { colors, resolvedTheme } = useTheme();
  const isDarkMode = resolvedTheme === "dark";
  const dispatch = useDispatch();

  // Get selected location for location-change detection
  const selectedLocation = useSelectedLocation();

  // Store initial location ID to detect changes after mount
  const initialLocationIdRef = useRef(selectedLocation?.id);

  // Keep a stable ref to onBackPress to avoid re-triggering the effect when
  // parent re-renders create a new function reference after the Redux dispatch
  const onBackPressRef = useRef(onBackPress);
  useEffect(() => { onBackPressRef.current = onBackPress; }, [onBackPress]);

  // Guard flag so redirection fires at most once
  const hasRedirectedRef = useRef(false);

  // Detect location changes and redirect back to ShiftHandover Dashboard
  useEffect(() => {
    const initialId = initialLocationIdRef.current;
    if (
      !hasRedirectedRef.current &&
      initialId &&
      selectedLocation?.id &&
      initialId !== selectedLocation.id
    ) {
      hasRedirectedRef.current = true;
      onBackPressRef.current();
    }
  }, [selectedLocation?.id]);

  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  // Use ScrollView ref for Animated.ScrollView
  const scrollViewRef = React.useRef<ScrollView | null>(null);
  const [showScrollToTop, setShowScrollToTop] = React.useState(false);
  const scrollButtonOpacity = React.useRef(new Animated.Value(0)).current;
  // Get the required state from Redux store
  const { loading, attendees, handoverState } = useSelector(
    (state: RootState) => state.shiftHandOver
  );

  const handleScroll = (event: any) => {
    console.log('[DEBUG] onScroll handler called'); // Log at top
    const { contentOffset } = event.nativeEvent;
    console.log('[DEBUG] onScroll fired, y:', contentOffset.y); // Debug log
    // Lower threshold for iPad, default 200 for others
    const threshold = isIpad ? 100 : 200;
    const shouldShow = contentOffset.y > threshold;
    if (shouldShow !== showScrollToTop) {
      setShowScrollToTop(shouldShow);
      Animated.timing(scrollButtonOpacity, {
        toValue: shouldShow ? 1 : 0,
        duration: 200,
        useNativeDriver: true,
      }).start();
    }
  };
  // Fetch questions and update state when component mounts
  useEffect(() => {
    if (isVisible) {
      // Fetch handover questions - this needs to be done first to populate the questions
      // that will be used by processToolsMapping
      // - new        -> org context only
      // - in-progress -> org context + recordId (walk ID)
      // - completed   -> skip org context (preserve original question set)
      const status = selectedHandover?.status?.toLowerCase().replace(' ', '-') as
        | 'new' | 'in-progress' | 'completed' | undefined;
      const currentHandoverState: 'new' | 'in-progress' | 'completed' =
        status === 'in-progress' || status === 'completed' ? status : 'new';
      const walkId = selectedHandover?.ID ?? null;
      fetchShiftHandoverQuestions({
        setIsLoading,
        setApiError,
        dispatch,
        handoverState: currentHandoverState,
        walkId,
      }).then(() => {
        // After questions are fetched, update state based on selected handover
        if (selectedHandover) {
          // Use the utility function to update the handover state
          updateShiftHandoverState(selectedHandover, dispatch);

          // Then fetch additional handover details from API
          fetchAndUpdateShiftHandoverDetails(
            selectedHandover,
            attendees,
            dispatch,
            setIsLoading,
            setApiError
          ).then(() => {
            // After fetching details, update selectedShiftHandOver in Redux so walkData is available
            dispatch(
              require("../../store/slices/shiftHandOverSlice").setSelectedShiftHandOver(
                selectedHandover
              )
            );
            // Now extract attendees from walkData in Redux
            const state = require("../../store").default.getState();
            const walkData =
              state.shiftHandOver.selectedShiftHandOver?.walkData;
            if (walkData && walkData.attendees) {
              console.log(
                "Extracting attendees from walkData:",
                walkData.attendees
              );
              const attendeeNames = walkData.attendees
                .split(",")
                .map((name: string) => name.trim())
                .filter(Boolean);
              if (attendeeNames.length > 0) {
                console.log("Attendees found:", attendeeNames);
                const attendeeObjs = attendeeNames.map(
                  (name: string, idx: number) => ({
                    id: String(idx),
                    displayName: name,
                    userPrincipalName: "",
                  })
                );
                dispatch(
                  require("../../store/slices/shiftHandOverSlice").setSelectedAttendees(
                    attendeeObjs
                  )
                );
              }
            }
          });
        }
      });
    }
  }, [isVisible, dispatch, selectedHandover, attendees]);
  // Back to Top button state and animation
  

  // Helper: is iPad (move inside component for correct scope)
  const isIpad = Platform.OS === "ios" && Dimensions.get("window").width >= 768;

  // Scroll handler for web
  React.useEffect(() => {
    if (Platform.OS === "web") {
      const handleWebScroll = () => {
        const shouldShow = window.scrollY > 200;
        if (shouldShow !== showScrollToTop) {
          setShowScrollToTop(shouldShow);
          Animated.timing(scrollButtonOpacity, {
            toValue: shouldShow ? 1 : 0,
            duration: 200,
            useNativeDriver: true,
          }).start();
        }
      };
      window.addEventListener("scroll", handleWebScroll);
      return () => window.removeEventListener("scroll", handleWebScroll);
    }
  }, [showScrollToTop, scrollButtonOpacity]);

  // Scroll to top logic (match PulseCheck/LeaderShipWalkRedirectScreen)
  const scrollToTop = () => {
    console.log('[DEBUG] Scroll-to-top button pressed'); // Log button press
    // Remove setTimeout and log the ref for debugging
    try {
      if (Platform.OS === "web") {
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      } else if (scrollViewRef.current) {
        console.log('ScrollView ref:', scrollViewRef.current);
        scrollViewRef.current.scrollTo({ x: 0, y: 0, animated: true });
      } else {
        console.log('ScrollView ref is null');
      }
    } catch (err) {
      console.error("Error scrolling to top:", err);
    }
  };

  // Log ref on mount for debugging
  React.useEffect(() => {
    if (Platform.OS !== 'web') {
      console.log('[DEBUG] scrollViewRef on mount:', scrollViewRef.current);
    }
  }, []);

  if (!isVisible) {
    return null;
  }

  return (
    <View
      style={{
        paddingHorizontal: Platform.OS === "web" ? "1.5%" : 16,
        flex: 1,
        backgroundColor: isDarkMode ? colors.background.page : "#F5F9FF",
        flexDirection: 'column'
      }}
    >
      {/* Header with dynamic height to accommodate multiple attendees */}
      <View style={{ zIndex: 2 }}>
        <ShiftHandOverDetailHeader
          logoSource={require("../../assets/pepsico.png")}
          onBackPress={onBackPress}
          onHomePress={onHomePress} // Pass down the prop
        />
      </View>
      <Animated.ScrollView
        ref={scrollViewRef}
        style={{ flex: 1, backgroundColor: isDarkMode ? colors.background.page : "#F5F9FF" }}
        contentContainerStyle={[
          styles.container,
          {
            minHeight: Dimensions.get('window').height * 1.5,
            backgroundColor: isDarkMode ? colors.background.page : "#F5F9FF",
          },
        ]}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={true}
        onScroll={Platform.OS === "web" ? undefined : handleScroll}
        scrollEventThrottle={16}
        keyboardDismissMode={Platform.OS === "ios" ? "on-drag" : undefined}
      >
        <View style={styles.innerContainer}>
          {/* Notification Banner (Figma style) - Only show for non-completed handovers */}
          {handoverState !== "completed" && (
            <View style={{
              width: '100%',
              backgroundColor: isDarkMode ? colors.background.surface : '#CCEAF6',
              borderWidth: isDarkMode ? 1 : 0,
              borderColor: isDarkMode ? colors.border.default : 'transparent',
              borderRadius: 4,
              // marginTop: 12,
              // marginBottom: 16,
              alignItems: 'center',
              justifyContent: 'flex-start', // align items to the left
              paddingVertical: 12,
              paddingHorizontal: 16,
              flexDirection: 'row',
            }}>
              {/* Info Icon */}
              <View style={{
                width: 20,
                height: 20,
                borderRadius: 10,
                backgroundColor: isDarkMode ? colors.brand.primary : '#0189B4',
                justifyContent: 'center',
                alignItems: 'center',
                marginRight: 8,
              }}>
                <Text style={{
                  color: '#fff',
                  fontSize: 14,
                  fontWeight: 'bold',
                  lineHeight: 20,
                }}>i</Text>
              </View>
              <Text style={{
                color: isDarkMode ? colors.text.primary : '#000000',
                fontSize: 14,
                fontFamily: 'Inter',
                fontWeight: '400',
                textAlign: 'left', // align text left
                letterSpacing: 0,
                lineHeight: 20,
                flex: 1,
              }}>
                Please select any behaviors or observations you see below
              </Text>
            </View>
          )}

          {/* Show loading indicator while fetching questions */}
          {(isLoading || loading) && (
            <View
              style={[
                styles.loadingContainer,
                isDarkMode && { backgroundColor: colors.background.page },
              ]}
            >
              <ActivityIndicator size="large" color="#0066CC" />
              <Text style={[styles.loadingText, isDarkMode && { color: colors.text.primary }]}>Loading questions...</Text>
            </View>
          )}

          {/* Display error if any */}
          {apiError && (
            <View
              style={[
                styles.errorContainer,
                isDarkMode && {
                  backgroundColor: colors.background.surface,
                  borderColor: colors.border.default,
                  borderLeftColor: colors.brand.primary,
                },
              ]}
            >
              <Text style={[styles.errorText, isDarkMode && { color: colors.text.primary }]}>Error: {apiError}</Text>
            </View>
          )}
          {/* Display shift handover questions component */}
          {!apiError && <ShiftHandoverQuestions isVisible={true} forceDisabled={forceDisabled} />}


          {/* Show Observation Notes Component */}
          <ShiftHandOverNotes forceDisabled={forceDisabled} />
          {/* Display Miscellaneous Observations component */}
          <ShiftHandOverMiscellaneousObservations forceDisabled={forceDisabled} />
          
          {/* Display Signature component */}
          <ShiftHandOverSignature forceDisabled={forceDisabled} />

          {/* Display summary of selected questions */}
          {/* {!apiError && !loading && <ShiftHandoverSummary />} */}

          {/* Action buttons (Save/Complete/PDF Export) */}
          <ShiftHandOverAction
            onComplete={(action) => {
              // For save action, no validation needed
              if (action === 'save') {
                return true;
              }
              
              // Set validation flag when Complete is clicked
              dispatch(require('../../store/slices/shiftHandOverSlice').setValidating(true));
              
              // Check if signature exists
              const state = require('../../store').default.getState();
              const { signatureFileName } = state.shiftHandOver;
              
              if (!signatureFileName) {
                Alert.alert(
                  'Validation Error',
                  'Please provide your signature before completing the handover.',
                  [{ text: 'OK' }]
                );
                return false;
              }
              
              // Clear validation flag if validation passes
              dispatch(require('../../store/slices/shiftHandOverSlice').setValidating(false));
              return true;
            }}
            onBackToDashboard={onBackPress}
            onShowMessage={(type, message) => {
              if (type === 'error') {
                setApiError(message);
              }
            }}
            selectedHandover={selectedHandover}
            forceDisabled={forceDisabled}
          />
        </View>
        {/* <Text style={styles.footer}>
          © 2025 PepsiCo | Shift Handover System v1.2
        </Text> */}
        {/* Dummy tall view for guaranteed scrollability during debugging */}
        <View  />
      </Animated.ScrollView>
      {/* Always render the button for debugging, always clickable */}
      <Animated.View
        style={[
          styles.scrollToTopButton,
          {
            opacity: 1, // Always visible for debug
            transform: [
              {
                scale: scrollButtonOpacity.interpolate({
                  inputRange: [0, 1],
                  outputRange: [0.8, 1],
                }),
              },
            ],
          },
        ]}
        pointerEvents={"auto"} // Always clickable for debug
      >
        <TouchableOpacity
          style={[
            styles.scrollToTopTouchable,
            isDarkMode && {
              backgroundColor: colors.background.surface,
              borderColor: colors.border.default,
            },
          ]}
          onPress={scrollToTop}
          activeOpacity={0.7}
          accessibilityLabel="Scroll to top"
          accessibilityRole="button"
        >
          <View style={styles.arrowIcon}>
            <Text style={[styles.arrowText, isDarkMode && { color: colors.brand.primary }]}>↑</Text>
          </View>
        </TouchableOpacity>
      </Animated.View>
      
      {/* Centralized Error Modal */}
      <ShiftHandoverErrorModal />
    </View>
  );
};

// Minimal test component for ScrollView onScroll on iOS/iPad
export const TestScrollView: React.FC = () => {
  const handleScroll = (event: any) => {
    const { contentOffset } = event.nativeEvent;
    console.log('[TEST] onScroll fired, y:', contentOffset.y);
  };
  return (
    <View style={{ flex: 1, backgroundColor: '#eee' }}>
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ minHeight: Dimensions.get('window').height * 2 }}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        showsVerticalScrollIndicator={true}
      >
        <Text style={{ fontSize: 24, margin: 20 }}>Scroll down to see logs</Text>
        <View style={{ height: 1200, backgroundColor: '#cceeff' }} />
        <Text style={{ fontSize: 24, margin: 20 }}>Bottom of ScrollView</Text>
      </ScrollView>
    </View>
  );
};

// Removed duplicate isIpad outside the component

const styles = StyleSheet.create({
  scrollContainer: {
    flex: 1,
    backgroundColor: "#F5F7FA",
  },
  container: {
    backgroundColor: "#F5F7FA",
    paddingBottom: 50,
    flexGrow: 1,
    alignItems: "stretch",
    // paddingHorizontal: Platform.OS === "web" ? "5%" : 16,
  },
  innerContainer: {
    // No card background, match PulseCheck
  },
  attendeeItem: {
    fontSize: 16,
    color: "#555",
    marginBottom: 6,
  },
  shiftHeader: {
    backgroundColor: "#0066CC",
  },
  sectionHeaderText: {
    fontSize: 16,
    color: "white",
  },
  // detailBox: {
  //   padding: 12,
  //   backgroundColor: "#f8f9fa",
  //   borderRadius: 6,
  //   // marginTop: 8,
  // },
  loadingContainer: {
    padding: 20,
    alignItems: "center",
    justifyContent: "center",
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: "#0066CC",
  },
  errorContainer: {
    padding: 14,
    borderRadius: 6,
    marginBottom: 16,
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    width: "100%",
    backgroundColor: "#f8d7da",
    borderColor: "#f5c6cb",
    borderLeftWidth: 5,
    borderLeftColor: "#dc3545",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 3,
  },
  errorText: {
    color: "#721c24",
    fontSize: 14,
    flex: 1,
  },
  questionsContainer: {
    marginTop: 16,
  },
  noQuestionsContainer: {
    padding: 20,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f8f9fa",
    borderRadius: 6,
    marginTop: 16,
  },
  noQuestionsText: {
    fontSize: 16,
    color: "#666",
    textAlign: "center",
  },
  footer: {
    textAlign: "center",
    marginTop: 40,
    color: "#868e96",
    fontSize: 12,
  },
  // Notes styles moved to ShiftHandOverNotes component

  // Add scroll-to-top button styles
  scrollToTopButton: {
    position: 'absolute',
    bottom: Platform.OS === 'web' ? 30 : (Platform.OS === 'ios' && Dimensions.get('window').width >= 768 ? 30 : 80),
    right: 20,
    zIndex: 1000,
  },
  scrollToTopTouchable: {
    backgroundColor: '#FFFFFF',
    borderRadius: 28,
    width: 56,
    height: 56,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 4.65,
    elevation: 8,
    borderWidth: 1,
    borderColor: '#E0E0E0',
  },
  arrowIcon: {
    width: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  arrowText: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#0066CC',
    lineHeight: 24,
  },
   instructionBannerContainer: {
    width: "100%",
  },
  instructionBanner: {
    backgroundColor: "#004F9F",
    color: "white",
    textAlign: "center",
    padding: 12,
    borderRadius: 4,
    // marginHorizontal: screenWidth < 600 ? 12 : 24,
    marginBottom: "0.5%",
  },
});

export default ShiftHandOverDetails;

